import React, { useEffect, useMemo, useState } from "react";

// import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { FileViewer } from "components/popups";
import { useInvestorsSelector, useTaskDealsSelect } from "store";
import { DealBase, FundFileType, IEmail } from "store/types";

import { FileType, fundFilesTypes } from "../../../../constants";

// import { ToastContent } from "./ToastContent";

interface IFileValues {
  file_id: number;
  type: FundFileType | null;
  investor_id: number | null;
  email_id: number;
}

interface Props {
  selectedEmails: IEmail[];
  initialSelectedDeal: DealBase;
  onCancel: () => void;
  onSubmit: ({ filesValues, selectedDeal }: { filesValues: IFileValues[]; selectedDeal: DealBase }) => void;
}

export const MultipleFilesCategorize: React.FC<Props> = ({
  selectedEmails,
  initialSelectedDeal,
  onCancel,
  onSubmit
}) => {
  const deals = useTaskDealsSelect();
  const investors = useInvestorsSelector();
  const [fileViewer, setFileViewer] = useState<{ id: number; url: string; name: string; type: FileType } | null>(null);
  const [localSelectedDeal, setLocalSelectedDeal] = useState<DealBase>(initialSelectedDeal);

  const [filesValues, setFilesValues] = useState<IFileValues[]>([]);

  const allEmailsFiles = useMemo(() => selectedEmails.flatMap((email) => email.attachments), [selectedEmails]);

  useEffect(() => {
    if (allEmailsFiles?.length) {
      setFilesValues(
        allEmailsFiles.map((file) => {
          return { file_id: file.id, type: null, investor_id: null, email_id: file.email_id! };
        })
      );
    } else {
      setFilesValues([]);
    }
  }, [allEmailsFiles]);

  const TableCellStyled = styled(TableCell)(() => ({
    padding: "6px 10px"
  }));

  const renderFileCategorizing = useMemo(() => {
    return (
      <Stack sx={{ width: "100%" }}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          sx={{
            gap: "15px",
            mb: "10px",
            padding: "0px 10px"
          }}
        >
          <Typography>Deal</Typography>
          <Autocomplete
            disableClearable
            sx={{ minWidth: 200 }}
            getOptionLabel={(option) => option.title}
            options={deals}
            value={localSelectedDeal}
            onChange={(_, option) => {
              if (option) {
                setLocalSelectedDeal(option);
              }
            }}
            renderOption={(props, item) => (
              <li {...props} key={item.id}>
                {item.title}
              </li>
            )}
            renderInput={(params) => <TextField {...params} placeholder="Select" />}
          />
        </Stack>
        <TableContainer sx={{ maxHeight: "500px" }}>
          <Table>
            <TableHead sx={{ bgcolor: "#F6F8FC" }}>
              <TableRow>
                <TableCellStyled>File name</TableCellStyled>
                <TableCellStyled>File type</TableCellStyled>
                <TableCellStyled>Investor</TableCellStyled>
              </TableRow>
            </TableHead>
            <TableBody>
              {allEmailsFiles.map((file, index) => (
                <TableRow key={index}>
                  <TableCellStyled
                    style={{ cursor: "pointer", color: "#26a6ae" }}
                    onClick={() =>
                      file.file_url &&
                      setFileViewer({ id: file.id, url: file.file_url, name: file.name, type: file.type })
                    }
                  >
                    {file.name}
                  </TableCellStyled>
                  <TableCellStyled>
                    <Autocomplete
                      sx={{ minWidth: 200 }}
                      options={fundFilesTypes}
                      value={filesValues.find((item) => item.file_id === file.id)?.type}
                      onChange={(_, option) => {
                        setFilesValues((prevState) =>
                          prevState.map((item) =>
                            item.file_id === file.id
                              ? {
                                  ...item,
                                  type: option
                                }
                              : item
                          )
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </TableCellStyled>
                  <TableCellStyled>
                    <Autocomplete
                      sx={{ minWidth: 200 }}
                      options={investors}
                      getOptionLabel={(option) => option.name}
                      value={investors.find(
                        (investor) => filesValues.find((item) => item.file_id === file.id)?.investor_id === investor.id
                      )}
                      onChange={(_, option) => {
                        setFilesValues((prevState) =>
                          prevState.map((item) =>
                            item.file_id === file.id
                              ? {
                                  ...item,
                                  investor_id: option?.id || null
                                }
                              : item
                          )
                        );
                      }}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </TableCellStyled>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    );
  }, [investors, allEmailsFiles, filesValues, localSelectedDeal, deals]);

  return (
    <Stack
      direction="column"
      justifyContent="space-between"
      sx={{
        ...style,
        backgroundColor: "#ffffff",
        borderRadius: "10px",
        width: "750px",
        minHeight: "400px",
        padding: "30px"
      }}
    >
      <Stack flexDirection="column" gap="20px">
        <Box>
          <Stack flexDirection="row" alignItems="center" justifyContent="space-between" padding="7px 0px">
            <Typography>Files</Typography>
            <CloseIcon sx={{ cursor: "pointer" }} onClick={onCancel} />
          </Stack>
          <Divider sx={{ borderColor: "rgba(219, 232, 245, 1)" }} />
        </Box>
        <Box sx={{ padding: "10px", color: "#0a3142" }}>{renderFileCategorizing}</Box>
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "flex-end", gap: "10px" }}>
        <Button variant="outlined" onClick={onCancel}>
          Close
        </Button>
        <Button
          sx={{ padding: "0 12px", backgroundColor: "#071F35" }}
          variant="contained"
          disabled={Boolean(!localSelectedDeal)}
          color="primary"
          onClick={() => {
            onSubmit({ filesValues, selectedDeal: localSelectedDeal });
          }}
          startIcon={<ForwardToInboxIcon />}
        >
          Associate
        </Button>
      </Stack>
      {fileViewer && (
        <FileViewer
          file={{ file_url: fileViewer.url, type: fileViewer.type }}
          isOpen={Boolean(fileViewer)}
          onClose={() => setFileViewer(null)}
        />
      )}
    </Stack>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px"
};
